/* HeroShared.jsx — port de hero-v3-shared.jsx (handoff "Hero A - dev 2")
 * Shared pieces for the compact-calculator hero.
 * Exports: Dropdown, PriceField, DotCar, TopBar, LeftCopy,
 *   ModeToggle, AICard, Sugs, PromptStrip, Thought, useHeroState,
 *   emptyState, ROW_OF, ROWS_ORDER, manualRows, MODE_COPY
 */
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  fmtMXN, useTypewriter, PLACEHOLDERS, Ico, suggestionsFor, IconForSug,
  useAI, versionsOf, getCarroceria, getMotor,
} from '../../core/heroCore';
import peicarLogo from '../../assets/logos/peicar-logo.svg';

/* ─── Row grouping (shared by all layouts) ────────────────────────────── */
export const ROW_OF = {
  marca: 'veh', modelo: 'veh', year: 'veh',
  version: 'ver', precio: 'ver',
  carroceria: 'tipo', motor: 'tipo',
  plan: 'plan',
  plazo: 'term', enganche: 'term',
};
export const ROWS_ORDER = ['veh', 'ver', 'tipo', 'plan', 'term'];

/* Manual progressive disclosure → which rows are unlocked */
export function manualRows(s) {
  const r = new Set(['veh']);
  if (s.marca && s.modelo && s.year) r.add('ver');
  if (s.version) r.add('tipo');
  if (s.carroceria && s.motor) r.add('plan');
  if (s.plan) r.add('term');
  return r;
}

export const MODE_COPY = { ai: 'Aria te ayuda', manual: 'Lo hago yo' };

/* ─── Custom dropdown ─────────────────────────────────────────────────── */
/* up: el menú abre hacia ARRIBA — para campos al fondo de la carta (p.ej.
 * Valor residual), donde hacia abajo lo recortaría el overflow del hero. */
export function Dropdown({ value, options, placeholder, onChange, disabled, compact, up }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const norm = options.map(o => typeof o === 'object' ? o : { value: o, label: o });
  const current = norm.find(o => o.value === value);
  const cls = `dd ${open ? 'open' : ''} ${value ? 'filled' : 'empty'} ${disabled ? 'disabled' : ''} ${compact ? 'compact' : ''}`;
  return (
    <div className={cls} ref={ref}>
      <button type="button" className="dd-btn" onClick={() => !disabled && setOpen(o => !o)} disabled={disabled}>
        <span className="dd-val">{current ? current.label : (placeholder || '—')}</span>
        <span className="dd-cv">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
        </span>
      </button>
      {open && !disabled && (
        <div className={`dd-menu${up ? ' up' : ''}`} role="listbox">
          {norm.map(o => (
            <button key={o.value} type="button"
              className={`dd-opt ${o.value === value ? 'on' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}
              role="option">
              <span>{o.label}</span>
              {o.extra ? <span className="extra">{o.extra}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Editable price field ────────────────────────────────────────────── */
/* placeholder/invalid/ariaLabel: opcionales (las primas del seguro manual los usan). */
export function PriceField({ value, onChange, disabled, placeholder = 'Precio', invalid, ariaLabel }) {
  const [focused, setFocused] = useState(false);
  const display = focused
    ? (value ? String(value) : '')
    : (value ? fmtMXN(value).replace('$', '') : '');
  return (
    <div className={`price-fld ${value ? 'filled' : 'empty'} ${disabled ? 'disabled' : ''} ${focused ? 'focused' : ''}${invalid ? ' invalid' : ''}`}>
      <input
        type="text" inputMode="numeric"
        value={display}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const n = parseInt(e.target.value.replace(/[^\d]/g, ''), 10);
          onChange(Number.isFinite(n) ? n : 0);
        }}
      />
    </div>
  );
}

/* ─── Heading glyph: WHEEL or GARAGE, alternating per entry ───────────── */
export function WheelGlyph() {
  return (
    <span className="dcg wheel" aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <defs>
          <linearGradient id="dcgW" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#204B7B" /><stop offset="52%" stopColor="#34C6F1" /><stop offset="100%" stopColor="#00BA6E" />
          </linearGradient>
        </defs>
        <g className="dc-tire">
          <circle cx="12" cy="12" r="11" fill="#0C2036" />
          <circle cx="12" cy="12" r="6.5" fill="url(#dcgW)" />
          <circle cx="12" cy="12" r="2.2" fill="#fff" />
          <rect x="11" y="1.5" width="2" height="5" rx="1" fill="#0C2036" />
          <rect x="11" y="17.5" width="2" height="5" rx="1" fill="#0C2036" />
          <rect x="1.5" y="11" width="5" height="2" rx="1" fill="#0C2036" />
          <rect x="17.5" y="11" width="5" height="2" rx="1" fill="#0C2036" />
        </g>
      </svg>
    </span>
  );
}
export function GarageGlyph() {
  return (
    <span className="dcg garage" aria-hidden="true">
      <svg viewBox="0 0 56 32">
        <defs>
          <linearGradient id="dcgGh" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#204B7B" /><stop offset="52%" stopColor="#34C6F1" /><stop offset="100%" stopColor="#00BA6E" />
          </linearGradient>
          <linearGradient id="dcgGv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34C6F1" /><stop offset="100%" stopColor="#00BA6E" />
          </linearGradient>
        </defs>
        <path d="M3 30 L3 12 Q3 8 7 6 L24 6 Q28 6 28 12 L28 30 Z" fill="none" stroke="url(#dcgGv)" strokeWidth="2.4" />
        <rect className="dc-door" x="5" y="9" width="21" height="20" rx="1.5" fill="url(#dcgGv)" opacity=".85" />
        <circle className="dc-dot" cx="15" cy="22" r="5.5" fill="url(#dcgGh)" />
        <g className="dc-car">
          <path d="M6 26 C6 23 9 22 12 21 L17 18 C19 17 22 17 25 17 L31 17 C35 17 38 19 40 22 L44 23 C45 24 45 25 45 26 L45 27 C45 28 44 28 43 28 L8 28 C7 28 6 27 6 26 Z" fill="url(#dcgGh)" />
          <path d="M18 20 L21 18 L31 18 L34 20 Z" fill="rgba(255,255,255,.34)" />
          <circle cx="15" cy="28" r="3.2" fill="#0C2036" />
          <circle cx="37" cy="28" r="3.2" fill="#0C2036" />
        </g>
      </svg>
    </span>
  );
}
export function DotCar() {
  // Alternate wheel ↔ garage on each entry (with a random first pick).
  const variant = useMemo(() => {
    let v;
    try {
      const last = localStorage.getItem('pei_heroGlyph');
      v = last === 'wheel' ? 'garage' : (last === 'garage' ? 'wheel' : (Math.random() < 0.5 ? 'wheel' : 'garage'));
      localStorage.setItem('pei_heroGlyph', v);
    } catch (e) {
      v = Math.random() < 0.5 ? 'wheel' : 'garage';
    }
    return v;
  }, []);
  return variant === 'garage' ? <GarageGlyph /> : <WheelGlyph />;
}

/* ─── Top bar ─────────────────────────────────────────────────────────── */
/* NOTA: exportado por compatibilidad con el handoff, pero NO se usa en la
 * página — el header viene del handoff de navegación (otro componente). */
export function TopBar() {
  return (
    <header className="h2-top">
      <div className="lg">
        <img src={peicarLogo} alt="Peicar" />
        <span className="tag">La llave a tu movilidad</span>
      </div>
      <nav className="nav">
        <a>Cotiza</a><a>Cómo funciona</a><a>Planes</a><a>Ayuda</a>
      </nav>
      <button className="cta">Iniciar sesión</button>
    </header>
  );
}

/* ─── Left copy ───────────────────────────────────────────────────────── */
export function LeftCopy() {
  return (
    <div className="h2-copy">
      <div className="h2-eyebrow"><span className="dot"></span> 100% mexicana · arrendamiento digital</div>
      <div className="h2-pre">Ahora es muy fácil</div>
      <h1 className="h2-h1">
        comprar tu <span className="grad">coche</span><DotCar />
      </h1>
      <p className="h2-sub">
        <b>Descríbelo.</b> Aria lo encuentra, arma tu plan y te entrega la renta mensual.
        Sin papeleo, todo en línea.
      </p>
      <div className="h2-trust">
        <div className="avs"><span>JM</span><span>SR</span><span>AL</span></div>
        <div>Los mexicanos ya usan <b>Peicar</b></div>
      </div>
    </div>
  );
}

/* ─── Mode toggle ─────────────────────────────────────────────────────── */
export function ModeToggle({ mode, onChange }) {
  return (
    <div className="h2-mode" role="tablist">
      <button className={mode === 'ai' ? 'on' : ''} onClick={() => onChange('ai')}>
        <span className="sp"><Ico.Sparkle /></span> {MODE_COPY.ai}
      </button>
      <button className={mode === 'manual' ? 'on' : ''} onClick={() => onChange('manual')}>
        <Ico.Edit /> {MODE_COPY.manual}
      </button>
    </div>
  );
}

/* ─── AI input card ───────────────────────────────────────────────────── */
export function AICard({ ai, autoFocusOnMount, small }) {
  const [focused, setFocused] = useState(autoFocusOnMount || false);
  const ref = useRef(null);
  const placeholder = useTypewriter(PLACEHOLDERS, { enabled: !focused && !ai.prompt && !ai.busy });
  useEffect(() => {
    if (autoFocusOnMount && ref.current) ref.current.focus({ preventScroll: true });
  }, [autoFocusOnMount]);
  const ready = ai.prompt.trim().length > 0 && !ai.busy;
  const klass = `ai-card ${ai.busy ? 'busy' : focused ? 'focused' : ''} ${small ? 'small' : ''}`;
  const handleKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (ready) ai.run(); } };
  return (
    <div className={klass}>
      <span className="ai-spark-bit b1"></span>
      <span className="ai-spark-bit b2"></span>
      <span className="ai-spark-bit b3"></span>
      <span className="ai-spark-bit b4"></span>
      <div className="ai-row">
        <div className="ai-spark"><Ico.Sparkle /><span className="ai-spark-ring"></span></div>
        <textarea
          ref={ref} rows={small ? 1 : 2}
          value={ai.prompt}
          placeholder={placeholder ? placeholder + '|' : 'Cuéntale a Aria qué coche quieres…'}
          onChange={(e) => ai.setPrompt(e.target.value)}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          onKeyDown={handleKey}
        />
        <button className={`ai-send ${ready ? 'armed' : ''}`} disabled={!ready} onClick={() => ai.run()} aria-label="Enviar"><Ico.Send /></button>
      </div>
      {!small && (
        <div className="ai-meta">
          <span className="pwr"><i></i> Aria · tu asesora Peicar · entiende cualquier descripción</span>
          <span className="sh">Enter para enviar</span>
        </div>
      )}
    </div>
  );
}

/* ─── Suggestion chips ────────────────────────────────────────────────── */
export function Sugs({ prompt, onPick }) {
  const sugs = useMemo(() => suggestionsFor(prompt), [prompt]);
  return (
    <div className="ai-sugs">
      {sugs.map((s) => (
        <button key={s.label} className="ai-sug" onClick={() => onPick(s.label)}>
          <span className="sp">{IconForSug(s.icon)}</span> {s.label}
        </button>
      ))}
    </div>
  );
}

/* ─── Compact "tu petición" strip (prompt + why, one bar) ─────────────── */
export function PromptStrip({ prompt, thought, busy, onEdit }) {
  return (
    <div className="pstrip">
      <div className="ps-top">
        <span className="ps-ic"><Ico.Sparkle /></span>
        <span className="ps-prompt">"{prompt}"</span>
        <button className="ps-edit" onClick={onEdit}><Ico.Edit /> Cambiar</button>
      </div>
      {(thought || busy) && (
        <div className="ps-why">{thought}{busy && <span className="cur" />}</div>
      )}
    </div>
  );
}

/* ─── Thought (standalone, used in thinking phase) ────────────────────── */
export function Thought({ text, busy }) {
  if (!text && !busy) return null;
  return (
    <div className="ai-thought">
      <span className="ic"><Ico.Sparkle width="16" height="16" /></span>
      <span>{text}{busy && <span className="cur" />}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   Shared hero state hook (mode, ai phase, calc state, visible rows)
   ═══════════════════════════════════════════════════════════════════════ */
/* placa: StatePlate.id (1 = CDMX, el DEFAULT_ADDRESS de rodacCatalog). Es de la
 * persona, no del auto: switchMode y restartAI la conservan. */
export function emptyState() {
  return { marca: '', modelo: '', version: '', year: '', carroceria: '', motor: '', plan: '', plazo: 0, enganche: 0, precio: 0, placa: 1 };
}

/* suggestRef (F4): ref opcional con el suggester real async — ver useAI. */
export function useHeroState(suggestRef = null) {
  const [mode, setMode] = useState('ai');
  const [aiPhase, setAiPhase] = useState('input'); // input | thinking | reveal
  const [state, setState] = useState(emptyState());
  const [aiRows, setAiRows] = useState(new Set());

  const setField = useCallback((patch) => setState(s => ({ ...s, ...patch })), []);

  const ai = useAI({
    suggestRef,
    onField: (k, v) => {
      setAiPhase(p => p === 'thinking' ? 'reveal' : p);
      setState(s => ({ ...s, [k]: v }));
      setAiRows(prev => new Set([...prev, ROW_OF[k]]));
    },
  });

  useEffect(() => { if (ai.busy) setAiPhase('thinking'); }, [ai.busy]);

  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m); setAiPhase('input'); setState(s => ({ ...emptyState(), placa: s.placa })); setAiRows(new Set());
    ai.reset(); ai.setPrompt('');
  };
  const restartAI = () => {
    setAiPhase('input'); setState(s => ({ ...emptyState(), placa: s.placa })); setAiRows(new Set());
    ai.reset(); ai.setPrompt('');
  };

  const visibleRows = mode === 'manual' ? manualRows(state) : aiRows;

  // Calc field helpers (cascade brand→model dependencies)
  const onMarca = (m) => setField({ marca: m, modelo: '', version: '', precio: 0, carroceria: '', motor: '', enganche: 0 });
  const onModelo = (mod) => setField({ modelo: mod, version: '', precio: 0, carroceria: '', motor: '', enganche: 0 });
  const onVersion = (v) => {
    const vs = versionsOf(state.marca, state.modelo);
    const x = vs.find(z => z[0] === v);
    setField({ version: v, precio: x ? x[1] : 0, enganche: 0, carroceria: getCarroceria(state.marca, state.modelo), motor: getMotor(state.marca, state.modelo) });
  };

  return { mode, aiPhase, state, setField, ai, switchMode, restartAI, visibleRows, onMarca, onModelo, onVersion };
}
